import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { FileText, Check, X, MessageSquare } from 'lucide-react'

const fmt = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)

const STATUS_CFG = {
  bozza:               { label: 'In preparazione', bg: 'bg-gray-500/10',   text: 'text-gray-400',   border: 'border-gray-500/20' },
  inviato:             { label: 'In attesa',        bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20' },
  accettato:           { label: 'Accettato',        bg: 'bg-green-500/10',  text: 'text-green-400',  border: 'border-green-500/20' },
  rifiutato:           { label: 'Rifiutato',        bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20' },
  modifiche_richieste: { label: 'Modifiche inviate',bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
}

// ─── RispostaModal ─────────────────────────────────────────────────────────────
const RispostaModal = ({ tipo, onConfirm, onClose }) => {
  const [messaggio, setMessaggio] = useState('')
  const isAccettato = tipo === 'accettato'

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-white mb-1">
          {isAccettato ? '✅ Accetta preventivo' : '🔄 Richiedi modifiche'}
        </h3>
        <p className="text-gray-500 text-sm mb-4">
          {isAccettato
            ? 'Aggiungi un messaggio opzionale di conferma.'
            : 'Descrivi le modifiche che vorresti apportare.'}
        </p>
        <textarea
          value={messaggio}
          onChange={e => setMessaggio(e.target.value)}
          rows={4}
          autoFocus
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none text-sm"
          placeholder={isAccettato ? 'Ottimo lavoro! Procediamo...' : 'Vorrei modificare...'}
        />
        <div className="flex gap-3 mt-4">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
            Annulla
          </button>
          <button
            onClick={() => onConfirm(messaggio)}
            className={`flex-1 py-2.5 rounded-lg text-white font-medium text-sm transition-colors ${
              isAccettato ? 'bg-green-600 hover:bg-green-500' : 'bg-orange-600 hover:bg-orange-500'
            }`}
          >
            {isAccettato ? 'Conferma accettazione' : 'Invia richiesta'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── DetailModal ───────────────────────────────────────────────────────────────
const DetailModal = ({ preventivo, onClose, onRisposta }) => {
  const [rispostaModal, setRispostaModal] = useState(null) // 'accettato' | 'modifiche_richieste'
  const [loading, setLoading] = useState(false)

  const voci = (preventivo.preventivi_voci || []).sort((a,b) => a.ordine - b.ordine)
  const totale = voci.reduce((s, v) => s + Number(v.quantita) * Number(v.prezzo_unitario), 0)
  const cfg = STATUS_CFG[preventivo.status] || STATUS_CFG.inviato
  const canRispondere = preventivo.status === 'inviato'

  const handleRisposta = async (tipo, messaggio) => {
    setLoading(true)
    const { data } = await supabase
      .from('preventivi')
      .update({
        status: tipo,
        messaggio_cliente: messaggio || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', preventivo.id)
      .select('*, progetti(titolo), preventivi_voci(*)')
      .single()

    if (data) {
      // notifica al team
      if (data.created_by) {
        const tipoNotif = tipo === 'accettato' ? 'revisione_approvata' : 'revisione_modifiche'
        const titoloNotif = tipo === 'accettato' ? '✅ Preventivo accettato' : '🔄 Modifiche richieste'
        await supabase.from('notifiche').insert({
          user_id: data.created_by,
          tipo: tipoNotif,
          titolo: titoloNotif,
          body: `"${data.titolo}"${messaggio ? ` — ${messaggio.slice(0,60)}` : ''}`,
          link: '/preventivi',
        })
      }
      onRisposta(data)
    }
    setLoading(false)
    setRispostaModal(null)
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 flex-shrink-0">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm font-mono">#{String(preventivo.numero).padStart(4,'0')}</span>
                <h2 className="text-lg font-bold text-white">{preventivo.titolo}</h2>
              </div>
              <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                {cfg.label}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Info */}
            <div className="grid grid-cols-2 gap-4">
              {preventivo.progetti && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Progetto</p>
                  <p className="text-sm text-white font-medium">{preventivo.progetti.titolo}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 mb-1">Validità</p>
                <p className="text-sm text-white font-medium">{preventivo.validita_giorni} giorni</p>
              </div>
              {preventivo.termini_pagamento && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Termini di pagamento</p>
                  <p className="text-sm text-white">{preventivo.termini_pagamento}</p>
                </div>
              )}
            </div>

            {/* Voci */}
            <div>
              <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 uppercase tracking-wider px-3 mb-2">
                <span className="col-span-6">Descrizione</span>
                <span className="col-span-2 text-right">Q.tà</span>
                <span className="col-span-2 text-right">€ Unit.</span>
                <span className="col-span-2 text-right">Totale</span>
              </div>
              <div className="bg-gray-800/40 rounded-xl divide-y divide-gray-800">
                {voci.map(v => (
                  <div key={v.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                    <span className="col-span-6 text-sm text-white">{v.descrizione}</span>
                    <span className="col-span-2 text-sm text-gray-400 text-right">{v.quantita}</span>
                    <span className="col-span-2 text-sm text-gray-400 text-right">{fmt(v.prezzo_unitario)}</span>
                    <span className="col-span-2 text-sm text-white font-medium text-right">{fmt(Number(v.quantita) * Number(v.prezzo_unitario))}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Totali */}
            <div className="ml-auto max-w-xs space-y-1.5">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Imponibile</span><span>{fmt(totale)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-400">
                <span>IVA 22%</span><span>{fmt(totale * 0.22)}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-white border-t border-gray-700 pt-2 mt-1">
                <span>Totale</span><span>{fmt(totale * 1.22)}</span>
              </div>
            </div>

            {/* Note */}
            {preventivo.note && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Note</p>
                <p className="text-sm text-gray-300 leading-relaxed">{preventivo.note}</p>
              </div>
            )}

            {/* Messaggio precedente */}
            {preventivo.messaggio_cliente && (
              <div className="bg-gray-800/60 rounded-xl px-4 py-3 flex items-start gap-3">
                <MessageSquare size={16} className="text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Il tuo messaggio precedente</p>
                  <p className="text-sm text-gray-300">{preventivo.messaggio_cliente}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer azioni */}
          {canRispondere && (
            <div className="px-6 py-4 border-t border-gray-800 flex gap-3">
              <button
                onClick={() => setRispostaModal('modifiche_richieste')}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-colors text-sm font-medium"
              >
                <MessageSquare size={16} /> Richiedi modifiche
              </button>
              <button
                onClick={() => setRispostaModal('accettato')}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white transition-colors text-sm font-medium"
              >
                <Check size={16} /> Accetta
              </button>
            </div>
          )}
        </div>
      </div>

      {rispostaModal && (
        <RispostaModal
          tipo={rispostaModal}
          onConfirm={(msg) => handleRisposta(rispostaModal, msg)}
          onClose={() => setRispostaModal(null)}
        />
      )}
    </>
  )
}

// ─── ClientPreventivi ──────────────────────────────────────────────────────────
const ClientPreventivi = () => {
  const { profile } = useAuth()
  const [preventivi, setPreventivi] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  const fetch = useCallback(async () => {
    if (!profile?.id) return
    const { data } = await supabase
      .from('preventivi')
      .select('*, progetti(titolo), preventivi_voci(*)')
      .eq('cliente_id', profile.id)
      .order('created_at', { ascending: false })
    setPreventivi(data || [])
    setLoading(false)
  }, [profile?.id])

  useEffect(() => { fetch() }, [fetch])

  const handleRisposta = (updated) => {
    setPreventivi(prev => prev.map(p => p.id === updated.id ? updated : p))
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Preventivi</h1>
        <p className="text-gray-500 text-sm mt-1">I preventivi inviati da Plutonic Studio</p>
      </div>

      {preventivi.length === 0 ? (
        <div className="text-center py-24">
          <FileText size={48} className="text-gray-700 mx-auto mb-4" />
          <p className="text-gray-600">Nessun preventivo ricevuto</p>
        </div>
      ) : (
        <div className="space-y-3">
          {preventivi.map(p => {
            const cfg = STATUS_CFG[p.status] || STATUS_CFG.inviato
            const voci = p.preventivi_voci || []
            const totale = voci.reduce((s, v) => s + Number(v.quantita) * Number(v.prezzo_unitario), 0)
            const canRispondere = p.status === 'inviato'

            return (
              <div
                key={p.id}
                onClick={() => setSelected(p)}
                className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors cursor-pointer"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg} border ${cfg.border}`}>
                  <FileText size={18} className={cfg.text} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-mono">#{String(p.numero).padStart(4,'0')}</span>
                    <p className="text-white font-medium text-sm truncate">{p.titolo}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {p.progetti && <span className="text-xs text-gray-500">{p.progetti.titolo}</span>}
                    <span className="text-xs text-gray-600">{new Date(p.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                {totale > 0 && (
                  <span className="text-sm font-bold text-white flex-shrink-0">{fmt(totale * 1.22)}</span>
                )}
                <div className="flex items-center gap-2">
                  {canRispondere && (
                    <span className="text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-1 rounded-lg">
                      Risposta richiesta
                    </span>
                  )}
                  <span className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                    {cfg.label}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {selected && (
        <DetailModal
          preventivo={selected}
          onClose={() => setSelected(null)}
          onRisposta={handleRisposta}
        />
      )}
    </div>
  )
}

export default ClientPreventivi
